import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Layers, X, CheckCircle2, Clock, Inbox, AlertTriangle } from 'lucide-react';
import type { Flow, Card, Colaborador, CampoConfig } from '../../types';

// Segmentação dos cards de um fluxo pelos campos de escolha (lista, checkbox,
// responsável...) — quantos, quantos resolvidos e em quanto tempo. Feito para
// os Chamados de TI (departamento, categoria, módulo, prioridade), mas lê os
// campos de qualquer fluxo.

interface Props {
  flow: Flow;
  cards: Card[];
  colaboradores: Colaborador[];
}

interface Dimensao {
  id: string;            // id do campo, ou '__etapa'
  nome: string;
  valores: (card: Card) => string[];
}

const COR_BARRA = '#3B82F6';
const COR_FILTRO = '#1D4ED8';
const TIPOS_ESCOLHA = new Set(['lista', 'checkbox', 'checkbox_single', 'checkbox_multi', 'responsavel']);
const SEM_VALOR = '(não informado)';

const PERIODOS: { id: string; label: string; dias: number | null }[] = [
  { id: '7', label: '7 dias', dias: 7 },
  { id: '30', label: '30 dias', dias: 30 },
  { id: '90', label: '90 dias', dias: 90 },
  { id: 'tudo', label: 'Tudo', dias: null },
];

/** Valor de campo em lista. Alguns cards guardam a lista como texto ("['A', 'B']"). */
function comoLista(v: any): string[] {
  if (v === null || v === undefined || v === '') return [];
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  const s = String(v).trim();
  if (s.startsWith('[') && s.endsWith(']')) {
    const miolo = s.slice(1, -1).trim();
    if (!miolo) return [];
    return miolo.split(',').map(x => x.trim().replace(/^['"]|['"]$/g, '').trim()).filter(Boolean);
  }
  return [s];
}

/** Data de criação: cards de portal só têm `dataCriacao` (UTC sem fuso). */
function criadoEm(c: Card): Date {
  const bruto: any = (c as any).createdAt || (c as any).dataCriacao;
  if (!bruto) return new Date(NaN);
  if (bruto instanceof Date) return bruto;
  const t = String(bruto);
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(t) ? t : t.replace(' ', 'T') + 'Z');
}

function horasEntre(a: Date, b: Date) {
  return Math.max(0, (b.getTime() - a.getTime()) / 36e5);
}

function fmtTempo(h: number | null) {
  if (h === null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1).replace('.', ',')} h`;
  return `${(h / 24).toFixed(1).replace('.', ',')} dias`;
}

const Segmentacao: React.FC<Props> = ({ flow, cards, colaboradores }) => {
  const [periodo, setPeriodo] = useState('30');
  const [filtros, setFiltros] = useState<Record<string, string>>({});   // dimensão -> valor (drill)

  const etapas = flow.etapas || [];
  const etapaFinal = etapas[etapas.length - 1];
  const nomeEtapa = useMemo(() => new Map(etapas.map(e => [e.id, e.nome])), [etapas]);
  const nomeColab = useMemo(() => new Map(colaboradores.map(c => [String(c.id), c.nome])), [colaboradores]);

  // Cards do fluxo criados no período
  const cardsPeriodo = useMemo(() => {
    const idsEtapas = new Set(etapas.map(e => e.id));
    const dias = PERIODOS.find(p => p.id === periodo)?.dias ?? null;
    const corte = dias ? Date.now() - dias * 864e5 : null;
    return cards.filter(c => {
      if (!idsEtapas.has(c.etapaId)) return false;
      if (!corte) return true;
      const t = criadoEm(c).getTime();
      return !isNaN(t) && t >= corte;
    });
  }, [cards, etapas, periodo]);

  // Tempo até a primeira entrada na etapa final (resolução)
  const resolucao = useMemo(() => {
    const m = new Map<string, number | null>();
    cardsPeriodo.forEach(c => {
      if (!etapaFinal || c.etapaId !== etapaFinal.id) { m.set(c.id, null); return; }
      const entradas = (c.etapaHistory || [])
        .filter(h => h.etapaId === etapaFinal.id && h.movedAt)
        .map(h => new Date(h.movedAt as any).getTime())
        .filter(t => !isNaN(t));
      const criado = criadoEm(c);
      m.set(c.id, entradas.length && !isNaN(criado.getTime()) ? horasEntre(criado, new Date(Math.min(...entradas))) : null);
    });
    return m;
  }, [cardsPeriodo, etapaFinal]);

  // Campos que dá para segmentar: os de escolha, mais textos curtos que se repetem (ex.: nome de quem abriu)
  const dimensoes = useMemo((): Dimensao[] => {
    const vistos = new Set<string>();
    const campos: CampoConfig[] = [];
    etapas.forEach(e => (e.campos || []).forEach(c => {
      if (!vistos.has(c.id)) { vistos.add(c.id); campos.push(c); }
    }));
    const lista: Dimensao[] = [
      { id: '__etapa', nome: 'Etapa atual', valores: c => [nomeEtapa.get(c.etapaId) || '—'] },
      { id: '__responsavel', nome: 'Responsável do card', valores: c => {
        const r = c.responsavelIndividualId ? String(c.responsavelIndividualId) : '';
        return r ? [nomeColab.get(r) || r] : [];
      } },
    ];
    campos.forEach(c => {
      const ler = (card: Card) => {
        const vals = comoLista(card.campos?.[c.id]);
        return c.tipo === 'responsavel' ? vals.map(v => nomeColab.get(v) || v) : vals;
      };
      if (TIPOS_ESCOLHA.has(c.tipo)) {
        lista.push({ id: c.id, nome: c.nome.replace(/:$/, ''), valores: ler });
      } else if (c.tipo === 'texto' && cardsPeriodo.length >= 5) {
        const preenchidos = cardsPeriodo.map(k => ler(k)[0]).filter(Boolean);
        const distintos = new Set(preenchidos.map(v => v.toLowerCase())).size;
        if (preenchidos.length >= cardsPeriodo.length * 0.5 && distintos <= preenchidos.length * 0.6) {
          lista.push({ id: c.id, nome: c.nome.replace(/:$/, ''), valores: ler });
        }
      }
    });
    return lista;
  }, [etapas, nomeEtapa, nomeColab, cardsPeriodo]);

  // Aplica o drill (clique numa barra filtra todos os painéis)
  const cardsFiltrados = useMemo(() => cardsPeriodo.filter(c =>
    Object.entries(filtros).every(([dim, valor]) => {
      const d = dimensoes.find(x => x.id === dim);
      if (!d) return true;
      const vals = d.valores(c);
      return valor === SEM_VALOR ? vals.length === 0 : vals.includes(valor);
    })
  ), [cardsPeriodo, filtros, dimensoes]);

  const resumo = useMemo(() => {
    const resolvidos = cardsFiltrados.filter(c => etapaFinal && c.etapaId === etapaFinal.id);
    const tempos = resolvidos.map(c => resolucao.get(c.id)).filter((t): t is number => t !== null && t !== undefined);
    const media = tempos.length ? tempos.reduce((a, b) => a + b, 0) / tempos.length : null;
    const ordenados = [...tempos].sort((a, b) => a - b);
    const mediana = ordenados.length ? ordenados[Math.floor(ordenados.length / 2)] : null;
    return { total: cardsFiltrados.length, resolvidos: resolvidos.length, abertos: cardsFiltrados.length - resolvidos.length, media, mediana };
  }, [cardsFiltrados, etapaFinal, resolucao]);

  const tabelaDe = (d: Dimensao) => {
    const m = new Map<string, { qtd: number; resolvidos: number; tempos: number[] }>();
    cardsFiltrados.forEach(c => {
      const vals = d.valores(c);
      (vals.length ? vals : [SEM_VALOR]).forEach(v => {
        if (!m.has(v)) m.set(v, { qtd: 0, resolvidos: 0, tempos: [] });
        const r = m.get(v)!;
        r.qtd += 1;
        if (etapaFinal && c.etapaId === etapaFinal.id) {
          r.resolvidos += 1;
          const t = resolucao.get(c.id);
          if (t !== null && t !== undefined) r.tempos.push(t);
        }
      });
    });
    return Array.from(m.entries())
      .map(([valor, r]) => ({
        valor, qtd: r.qtd, resolvidos: r.resolvidos, abertos: r.qtd - r.resolvidos,
        pct: cardsFiltrados.length ? (r.qtd / cardsFiltrados.length) * 100 : 0,
        tempoMedio: r.tempos.length ? r.tempos.reduce((a, b) => a + b, 0) / r.tempos.length : null,
      }))
      .sort((a, b) => b.qtd - a.qtd);
  };

  const alternarFiltro = (dim: string, valor: string) => setFiltros(f => {
    const n = { ...f };
    if (n[dim] === valor) delete n[dim]; else n[dim] = valor;
    return n;
  });

  const Kpi = ({ icon, label, valor, sub, cor }: { icon: React.ReactNode; label: string; valor: string; sub?: string; cor: string }) => (
    <div className="bg-white rounded-xl p-4 border border-gray-200/70 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
        <span className={cor}>{icon}</span> {label}
      </div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{valor}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <Layers className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Segmentação por campos</h3>
            <p className="text-xs text-gray-500">Cards criados no período · clique numa barra para filtrar todos os painéis</p>
          </div>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg ml-auto">
          {PERIODOS.map(p => (
            <button key={p.id} onClick={() => setPeriodo(p.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${periodo === p.id ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {Object.keys(filtros).length > 0 && (
          <div className="w-full flex flex-wrap items-center gap-2">
            {Object.entries(filtros).map(([dim, valor]) => (
              <button key={dim} onClick={() => alternarFiltro(dim, valor)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100">
                {dimensoes.find(d => d.id === dim)?.nome}: {valor} <X className="w-3 h-3" />
              </button>
            ))}
            <button onClick={() => setFiltros({})} className="text-xs text-gray-500 hover:text-gray-800 underline">limpar filtros</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<Inbox className="w-4 h-4" />} label="Abertos no período" valor={String(resumo.total)} cor="text-blue-600" />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label={`Em "${etapaFinal?.nome || 'última etapa'}"`} valor={String(resumo.resolvidos)}
          sub={resumo.total ? `${Math.round((resumo.resolvidos / resumo.total) * 100)}% do total` : undefined} cor="text-green-600" />
        <Kpi icon={<AlertTriangle className="w-4 h-4" />} label="Ainda em andamento" valor={String(resumo.abertos)} cor="text-amber-600" />
        <Kpi icon={<Clock className="w-4 h-4" />} label="Tempo médio até resolver" valor={fmtTempo(resumo.media)}
          sub={resumo.mediana !== null ? `mediana ${fmtTempo(resumo.mediana)} · horas corridas` : 'horas corridas'} cor="text-purple-600" />
      </div>

      {resumo.total === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/50 p-10 text-center text-sm text-gray-500">Nenhum card criado no período.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {dimensoes.map(d => {
            const linhas = tabelaDe(d);
            const grafico = linhas.slice(0, 12);
            const ativo = filtros[d.id];
            return (
              <div key={d.id} className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">{d.nome}</h4>
                  <span className="text-xs text-gray-400">{linhas.length} valor(es)</span>
                </div>
                <div style={{ height: Math.max(120, grafico.length * 28 + 20) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grafico} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis type="category" dataKey="valor" width={140} tick={{ fontSize: 11, fill: '#334155' }}
                        tickFormatter={(v: string) => (v.length > 22 ? v.slice(0, 21) + '…' : v)} />
                      <Tooltip formatter={(v: any, n: string) => [v, n === 'qtd' ? 'Cards' : n]} cursor={{ fill: '#f1f5f9' }} />
                      <Bar dataKey="qtd" radius={[0, 4, 4, 0]} onClick={(e: any) => e?.valor && alternarFiltro(d.id, e.valor)} style={{ cursor: 'pointer' }}>
                        {grafico.map(l => <Cell key={l.valor} fill={ativo === l.valor ? COR_FILTRO : COR_BARRA} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500 sticky top-0 bg-white">
                      <tr className="border-b border-gray-100">
                        <th className="text-left font-medium py-1.5">{d.nome}</th>
                        <th className="text-right font-medium">Cards</th>
                        <th className="text-right font-medium">%</th>
                        <th className="text-right font-medium">Resolvidos</th>
                        <th className="text-right font-medium">Em andamento</th>
                        <th className="text-right font-medium">Tempo médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map(l => (
                        <tr key={l.valor} onClick={() => alternarFiltro(d.id, l.valor)}
                          className={`border-b border-gray-50 cursor-pointer hover:bg-blue-50/50 ${ativo === l.valor ? 'bg-blue-50' : ''}`}>
                          <td className="py-1.5 text-gray-800">{l.valor}</td>
                          <td className="text-right font-semibold text-gray-900">{l.qtd}</td>
                          <td className="text-right text-gray-600">{l.pct.toFixed(0)}%</td>
                          <td className="text-right text-green-700">{l.resolvidos}</td>
                          <td className="text-right text-amber-700">{l.abertos}</td>
                          <td className="text-right text-gray-700">{fmtTempo(l.tempoMedio)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Segmentacao;
